import { FavouriteUser } from '../generated/gql-types';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { favouriteActions } from './favourite.action';

interface FavouriteState {
  favouriteUsers: FavouriteUser[];
  isLoading: boolean;
  error: string | null;
  fetchFavouriteUsers: (clientId: string) => Promise<void>;
  addFavouriteUser: (clientId: string, userId: string) => Promise<void>;
  removeFavouriteUser: (clientId: string, userId: string) => Promise<void>;
  getFavouriteUsersByClientId: (clientId: string) => FavouriteUser[];
}

export const useFavouriteStore = create<FavouriteState>()(
  persist(
    (set, get) => ({
      favouriteUsers: [],
      isLoading: false,
      error: null,
      fetchFavouriteUsers: async (clientId: string) => {
        try {
          set({ isLoading: true });
          const favouriteUsers =
            await favouriteActions.getFavouriteUsers(clientId);
          set({ favouriteUsers, isLoading: false });
        } catch (error) {
          console.error('Error fetching favourite users:', error);
          set({ error: 'Failed to fetch favourite users', isLoading: false });
          throw error;
        }
      },
      addFavouriteUser: async (clientId: string, userId: string) => {
        try {
          set({ isLoading: true });
          const success = await favouriteActions.addFavouriteUser(
            clientId,
            userId,
          );
          set({ isLoading: false });
          if (success) {
            set({
              favouriteUsers: [...get().favouriteUsers, { clientId, userId }],
            });
          } else {
            set({ error: 'Failed to add favourite user' });
          }
        } catch (error) {
          console.error('Error adding favourite user:', error);
          set({ error: 'Failed to add favourite user', isLoading: false });
          throw error;
        }
      },
      removeFavouriteUser: async (clientId: string, userId: string) => {
        try {
          set({ isLoading: true });
          const success = await favouriteActions.removeFavouriteUser(
            clientId,
            userId,
          );
          set({ isLoading: false });
          if (success) {
            set({
              favouriteUsers: get().favouriteUsers.filter(
                (user) => user.userId !== userId,
              ),
            });
          } else {
            set({ error: 'Failed to remove favourite user' });
          }
        } catch (error) {
          console.error('Error removing favourite user:', error);
          set({ error: 'Failed to remove favourite user', isLoading: false });
          throw error;
        }
      },
      getFavouriteUsersByClientId: (clientId: string) => {
        return get().favouriteUsers?.filter(
          (user) => user.clientId === clientId,
        );
      },
    }),
    {
      name: 'favourite',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        favouriteUsers: state.favouriteUsers,
      }),
    },
  ),
);
